import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react'
import { authMe, authLogin, authRegister, authCompleteRegistration, authGoogle, authLogout, authLogoutAll, authRefresh, hasBearerToken, isNetworkError, recoverSession } from '../lib/api'

interface AuthUser {
  id: number
  name: string
  email: string
  phone?: string | null
  role: string
  is_onboarded: boolean
  is_superuser: boolean
  /** Effective permission strings for the user's role (e.g. "screens:edit").
   *  Drives the usePermissions hook; backend stays authoritative. */
  permissions?: string[]
  /** app_kinds this user is scoped to; empty/undefined = no product-app
   *  access (confined to My) unless the role is owner/admin. */
  app_scope?: string[]
  appearance?: 'light' | 'dark' | 'system'
  /** Accent preset key; see lib/accents. Absent/unknown → monochrome. */
  accent?: string | null
  /** Display language. Null/undefined = never chosen → keep the local choice. */
  language?: 'en' | 'ar' | null
}

interface AuthContextValue {
  user: AuthUser | null
  /** True if the backend's `wetook_logged_in` marker cookie is present. Lets
   *  callers know "the user is signed in" without waiting for /auth/me. */
  hasSession: boolean
  loading: boolean
  login: (email: string, password: string, orgNo: string, remember: boolean) => Promise<AuthUser>
  register: (name: string, orgName: string, email: string, password: string, phone?: string) => Promise<AuthUser>
  /** Finish sign-up by verifying the emailed link. The `token` carries the
   *  verified email plus the name/password captured on the sign-up form, so no
   *  further details are collected here. Provisions the org and signs the user
   *  in (sets the same session state as `register`). */
  completeRegistration: (token: string) => Promise<AuthUser>
  /** Sign in or sign up with a Google ID token (the `credential` from Google
   *  Identity Services). One call handles both — the backend find-or-creates. */
  loginWithGoogle: (credential: string, remember: boolean) => Promise<AuthUser>
  logout: () => Promise<void>
  /** Revokes every session for this account — all devices/browsers, including
   *  this one (token_version bump kills the current access token too). */
  logoutAll: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

// Schedule the next /auth/refresh this many seconds before the access cookie
// expires. Picks up the new exp from the refresh response and reschedules.
const REFRESH_LEAD_SECONDS = 60

/**
 * The access token's expiry on THIS device's clock, in epoch seconds.
 *
 * The server sends `expires_in` (seconds from now) beside `expires_at` (an
 * instant on the server's clock). Only the duration is safe to schedule from:
 * comparing the server's instant to Date.now() on a device whose clock is off
 * fires the refresh at the wrong time, and a till whose clock ran ahead
 * refreshed the moment it signed in, failed (no refresh cookie in the Android
 * shell) and was thrown to the login page, which the shell turns into the
 * device-setup screen. `expires_at` stays as the fallback for an older backend.
 */
function localExpiry(data: { expires_at?: number | null; expires_in?: number | null } | null | undefined): number | null {
  if (typeof data?.expires_in === 'number') return Math.floor(Date.now() / 1000) + data.expires_in
  return typeof data?.expires_at === 'number' ? data.expires_at : null
}

// JS-readable marker cookie set by the backend on login/register/refresh.
// Presence alone proves the visitor has a session — no /auth/me call needed.
const LOGGED_IN_COOKIE = 'wetook_logged_in'

/**
 * The last identity the server confirmed.
 *
 * Needed because `hasSession` alone is not enough to render an app: the
 * entitlement gate and every permission check read `user`, so a till whose
 * `/auth/me` cannot complete sits on a loading screen forever even though its
 * session is perfectly valid. Offline, that is a register that boots to a
 * spinner during the outage it was built to trade through.
 *
 * Restored ONLY when the probe failed for transport reasons, and cleared on a
 * real 401 or a logout. It grants nothing the backend has not already granted
 * — every API call still authenticates, and the permissions in here are
 * re-checked server-side on each one — so the worst a tampered copy buys is a
 * UI that offers a button the server then refuses.
 */
const USER_CACHE_KEY = 'wetook.auth.user'

function readCachedUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY)
    return raw ? JSON.parse(raw) as AuthUser : null
  } catch { return null }
}

function writeCachedUser(user: AuthUser | null): void {
  try {
    if (user) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user))
    else localStorage.removeItem(USER_CACHE_KEY)
  } catch { /* private mode */ }
}

function readMarker(): boolean {
  if (typeof document === 'undefined') return false
  return document.cookie.split('; ').some(c => c.startsWith(`${LOGGED_IN_COOKIE}=1`))
}

/**
 * Is a person already signed in, as far as this browser can tell?
 *
 * Exported for code that runs BEFORE React does — Pocket's boot decides where
 * an unpaired device should land, and it has to know whether anyone is signed
 * in without mounting the provider that would normally answer.
 *
 * The marker is a hint, not an authority: it mirrors the refresh cookie's
 * lifetime and says nothing about whether the session is still valid. That is
 * the right strength here, where the question is only "should this look like a
 * fresh device or like someone's browser".
 */
export function hasSessionMarker(): boolean {
  return readMarker()
}

/**
 * Does this client hold a session AT ALL — by cookie or by Bearer token?
 *
 * Every gate below used to ask `readMarker()` directly, which quietly assumed
 * the only way to hold a session is to have the backend's marker cookie. That
 * is true of every app served from wetook.net and false for exactly one
 * caller: the Android shell, whose pages come from inside the APK, making the
 * marker a third-party cookie its WebView will not store.
 *
 * The result was a device that paired, minted a real session, held a valid
 * Bearer token — and was bounced to a login page anyway, because the one
 * question asked was about a cookie it could never have.
 */
function hasSessionCredential(): boolean {
  return readMarker() || hasBearerToken()
}

export function AuthProvider({ children, offlineFirst = false }: {
  children: ReactNode
  /**
   * Start from the last known user instead of waiting for /auth/me.
   *
   * For the till (Pocket), which must open with no network. Normally the app
   * renders nothing until /auth/me answers; offline that means waiting for it
   * to FAIL, which on wifi with no route out is a hang, not a quick error —
   * and the register sat behind it. With this, a device that has a session
   * credential and a cached identity renders at once and confirms in the
   * background: a 401 still signs it out, a network error changes nothing.
   * Off for every other app, where showing a stale identity for a moment buys
   * nothing.
   */
  offlineFirst?: boolean
}) {
  // Resolved once, at mount: offline-first only applies to a device that was
  // already signed in here (credential AND a cached identity to show).
  const [startCached] = useState(() => (offlineFirst && hasSessionCredential() ? readCachedUser() : null))
  const [user, setUser]       = useState<AuthUser | null>(startCached)
  const [hasSession, setHasSession] = useState<boolean>(hasSessionCredential)
  const [expiresAt, setExpiresAt] = useState<number | null>(null)
  const [loading, setLoading] = useState(!startCached)
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // On mount, verify the session via /auth/me. The HttpOnly access cookie is
  // sent automatically (withCredentials=true in api.ts). If the cookie is
  // expired, the api interceptor will attempt one /auth/refresh via the
  // HttpOnly refresh cookie before giving up.
  useEffect(() => {
    let cancelled = false
    const bootstrap = async () => {
      // ── DEV-ONLY auto-login ────────────────────────────────────────────
      // Skips the login screen during local development by signing in with
      // seed credentials before the /auth/me check. SECURITY: the whole block
      // is gated on `import.meta.env.DEV`, which Vite statically replaces with
      // `false` in any production/staging build — the bundler then dead-code-
      // eliminates this branch entirely, so neither the logic nor the
      // credentials ever ship to a deployed bundle. It is additionally opt-in
      // per run via the VITE_DEV_AUTOLOGIN flag, which is set only by
      // scripts/dev-auth.ps1 (never in any committed .env), so a normal
      // `npm run dev` is unaffected.
      if (
        import.meta.env.DEV &&
        import.meta.env.VITE_DEV_AUTOLOGIN === '1' &&
        !hasSessionCredential()
      ) {
        try {
          await authLogin({
            email: import.meta.env.VITE_DEV_EMAIL || 'owner@demo.test',
            password: import.meta.env.VITE_DEV_PASSWORD || 'password123',
            remember_me: false,
          })
        } catch {
          // Auto-login failed (backend down, creds changed) — fall through to
          // the normal /auth/me path, which bounces to the login screen.
        }
      }

      // No session marker → the visitor isn't signed in. Skip /auth/me
      // entirely: an unauthenticated call 401s, and the api interceptor
      // reacts to a terminal 401 by redirecting the whole window to
      // ${AUTH_URL}/login. On a public page rendered under this provider
      // (e.g. the /m/:slug Ping preview embedded in the admin builder) that
      // hijacks the iframe — the "preview" navigates to the login screen.
      // The marker mirrors the refresh cookie's lifetime, so its absence is
      // an authoritative "no session" and we can short-circuit safely.
      if (!hasSessionCredential()) {
        if (!cancelled) { setHasSession(false); setLoading(false) }
        return
      }

      try {
        const r = await authMe()
        if (cancelled) return
        const { expires_at, expires_in, ...userFields } = r.data
        setUser(userFields)
        writeCachedUser(userFields)
        setHasSession(true)
        setExpiresAt(localExpiry({ expires_at, expires_in }))
      } catch (err: any) {
        if (cancelled) return
        // A request that never REACHED the server says nothing about whether
        // the session is alive, and treating it as a logout is how an offline
        // till gets thrown to a login page it also cannot reach. That is not a
        // hypothetical: a register or a kitchen display reloads during an
        // outage — a power cut, a kiosk restart, a cook pulling the plug — and
        // used to come back to the auth redirect with its cached board and its
        // queued sales stranded behind it.
        //
        // The marker cookie is already the authority here ("presence alone
        // proves the visitor has a session", above) and it mirrors the refresh
        // cookie's lifetime, so trusting it through a transport failure grants
        // nothing the backend has not already granted. Every actual API call
        // still authenticates; all this decides is whether we bounce to a
        // login screen.
        //
        // A real 401 is different — that IS the server saying the session is
        // over — and still clears it.
        if (isNetworkError(err)) {
          const known = readCachedUser()
          setHasSession(hasSessionCredential())
          // Without this the app renders behind RequireAuth and then stops at
          // the entitlement gate, which waits on `user`. A session with no
          // identity is a spinner.
          if (known) setUser(known)
          return
        }
        // 401/expired — the marker (if any) is stale; clear it so RequireAuth
        // bounces to login instead of trusting a dead session.
        setHasSession(false)
        writeCachedUser(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    bootstrap()
    return () => { cancelled = true }
  }, [])

  // Proactive refresh: schedule a /auth/refresh ~60s before the access cookie
  // expires. The refresh response carries the new exp and reschedules. On
  // failure, drop user state so RequireAuth bounces to login.
  useEffect(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current)
      refreshTimer.current = null
    }
    if (!user || !expiresAt) return

    const msUntilRefresh = (expiresAt - REFRESH_LEAD_SECONDS) * 1000 - Date.now()
    // Already past the lead-time window: refresh soon, but never in a tight
    // loop. A floor of a few seconds means a deadline that still reads as
    // past after a refresh (an older backend, a wildly wrong clock) costs one
    // request every few seconds at worst, not one per render.
    const delay = Math.max(5_000, msUntilRefresh)

    refreshTimer.current = setTimeout(async () => {
      try {
        const { data } = await authRefresh()
        if (data?.user) setUser(data.user)
        setExpiresAt(localExpiry(data) ?? localExpiry(data?.user))
        return
      } catch {
        /* fall through: there may be another way back */
      }
      // A paired till signs back in with its device token instead of being
      // signed out. Inside the Android shell this is the ONLY way back: the
      // cookie refresh above cannot work there, so without this every till
      // was dropped to the setup screen once its first access token aged out.
      if (await recoverSession()) {
        try {
          const r = await authMe()
          const { expires_at, expires_in, ...userFields } = r.data
          setUser(userFields)
          writeCachedUser(userFields)
          setExpiresAt(localExpiry({ expires_at, expires_in }))
          return
        } catch {
          /* fall through to signing out */
        }
      }
      setUser(null)
      setExpiresAt(null)
    }, delay)

    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current)
    }
  }, [user, expiresAt])

  const login = async (email: string, password: string, _orgNo: string, remember: boolean) => {
    const { data } = await authLogin({ email, password, remember_me: remember })
    setUser(data.user)
    setHasSession(true)
    setExpiresAt(localExpiry(data))
    return data.user as AuthUser
  }

  const register = async (name: string, orgName: string, email: string, password: string, phone?: string) => {
    const { data } = await authRegister({ name, org_name: orgName, email, password, phone })
    setUser(data.user)
    setHasSession(true)
    setExpiresAt(localExpiry(data))
    return data.user as AuthUser
  }

  const completeRegistration = async (token: string) => {
    const { data } = await authCompleteRegistration({ token })
    setUser(data.user)
    setHasSession(true)
    setExpiresAt(localExpiry(data))
    return data.user as AuthUser
  }

  const loginWithGoogle = async (credential: string, remember: boolean) => {
    const { data } = await authGoogle({ credential, remember_me: remember })
    setUser(data.user)
    setHasSession(true)
    setExpiresAt(localExpiry(data))
    return data.user as AuthUser
  }

  const logout = async () => {
    try {
      await authLogout()
    } catch {
      // best-effort — clear client state regardless
    }
    setUser(null)
    writeCachedUser(null)
    setHasSession(false)
    setExpiresAt(null)
  }

  const logoutAll = async () => {
    try {
      await authLogoutAll()
    } catch {
      // best-effort — clear client state regardless
    }
    setUser(null)
    writeCachedUser(null)
    setHasSession(false)
    setExpiresAt(null)
  }

  return (
    <AuthContext.Provider value={{ user, hasSession, loading, login, register, completeRegistration, loginWithGoogle, logout, logoutAll }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

/** Like {@link useAuth} but returns null instead of throwing when no
 *  AuthProvider is mounted. For the universal providers (Theme/Language) that
 *  also render on public pages (the per-brand Ping storefront) which deliberately
 *  have no staff AuthProvider above them. */
export function useOptionalAuth() {
  return useContext(AuthContext)
}
